import { cva, type VariantProps } from 'class-variance-authority';

// Centralized Tailwind class definitions for the KaCombobox component family (mirrors
// `button-variants.ts` / `select-variants.ts`). Components compose these with `cnMerge(...)` so
// consumer-supplied classes (anchorClasses, optionsContainerClasses, etc.) still override via
// tailwind-merge.
//
// KaCombobox and KaSelect render the same dropdown/option UI, so the option row, group, group
// label, icon slot, scroll viewport and clear button styling are SHARED — re-exported from
// `select-variants.ts` under combobox-friendly names so there is a single source of truth. Editing
// those definitions updates both components. `selectOptionVariants` carries
// `focus:outline-none focus-visible:outline-none`, which is a harmless no-op here: the combobox
// listbox is non-modal and drives selection via `aria-activedescendant`, so its options never take
// DOM focus.
export {
  selectClearButtonVariants as comboboxClearButtonVariants,
  selectGroupLabelVariants as comboboxGroupLabelVariants,
  selectGroupVariants as comboboxGroupVariants,
  selectIconSlotVariants as comboboxIconSlotVariants,
  selectOptionVariants as comboboxOptionVariants,
  selectViewportVariants as comboboxViewportVariants,
} from '../KaSelect/select-variants';

// Anchor shell (wraps the text input + tags). Unlike KaSelect's focusable trigger, the focusable
// element is the inner input, so focus styling keys off `has-[:focus]` rather than `focus-visible`.
export const comboboxAnchorVariants = cva(
  'relative flex min-h-[36px] w-full min-w-[160px] cursor-pointer items-center gap-x-2 rounded bg-white px-3 py-1.5 text-left text-sm leading-6 shadow-sm ring-1 ring-inset has-[:focus]:outline-none has-[:focus]:ring-2 has-[:focus]:ring-inset dark:bg-navy-800 dark:text-gray-50',
  {
    variants: {
      invalid: {
        true: '!has-[:focus]:ring-red-500/80 !ring-red-500/60',
        false:
          'ring-gray-300 has-[:focus]:ring-blue-400 dark:ring-navy-600 dark:has-[:focus]:ring-blue-500',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50',
        false: '',
      },
      open: { true: '', false: '' },
    },
    compoundVariants: [
      // Highlight the anchor while the listbox is open, unless it's showing the invalid (red) ring.
      { open: true, invalid: false, class: 'ring-blue-400 dark:ring-blue-500' },
    ],
    defaultVariants: { invalid: false, disabled: false, open: false },
  },
);

// Text input inside the anchor. `layout` distinguishes the multi-select input (flexes among the
// tags) from the single-select input (fills the row); everything else — including the placeholder
// tones — is shared so both inputs look identical apart from layout.
export const comboboxInputVariants = cva(
  'm-0 truncate bg-transparent p-0 text-left text-sm leading-6 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
  {
    variants: {
      layout: { multiple: 'flex-1', single: 'block w-full' },
      invalid: {
        true: 'text-red-600 placeholder:text-red-500 dark:text-red-400',
        false: 'text-gray-900 dark:text-gray-50',
      },
      hasSelection: { true: '', false: '' },
      disabled: { true: 'cursor-not-allowed', false: '' },
    },
    compoundVariants: [
      {
        invalid: false,
        hasSelection: true,
        class: 'placeholder:text-gray-700 dark:placeholder:text-gray-100',
      },
      {
        invalid: false,
        hasSelection: false,
        class: 'placeholder:text-gray-600 dark:placeholder:text-gray-400',
      },
    ],
    defaultVariants: { layout: 'single', invalid: false, hasSelection: false, disabled: false },
  },
);

// Listbox popover content. Same as KaSelect's content except the reka popper width var and the
// combobox transform-origin var; the prop-driven z-index is merged in the template.
export const comboboxContentVariants = cva(
  'mt-1 max-h-[40vh] w-[var(--reka-popper-anchor-width)] min-w-[160px] max-w-[var(--reka-popper-anchor-width)] overflow-hidden rounded bg-white text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-navy-900 dark:text-gray-50 dark:ring-navy-600 data-[state=closed]:animate-[scale-out_100ms_ease-in] data-[state=open]:animate-[scale-in_100ms_ease-out] origin-[var(--reka-combobox-content-transform-origin)]',
);

// "Create option" row (KaComboboxCreateOption) — a distinct highlight treatment from a normal
// option row (no bottom border, different highlighted/checked backgrounds).
export const comboboxCreateOptionVariants = cva(
  'group relative m-0 w-full cursor-pointer select-none list-none py-2 pl-3 pr-2 data-[disabled]:cursor-not-allowed data-[highlighted]:data-[disabled]:!bg-transparent data-[state=checked]:bg-blue-50 data-[state=unchecked]:data-[highlighted]:bg-gray-100 data-[disabled]:opacity-50 dark:data-[highlighted]:data-[disabled]:!bg-transparent dark:data-[state=checked]:bg-blue-400/20 dark:data-[state=unchecked]:data-[highlighted]:bg-navy-800',
);

export type KaComboboxAnchorVariants = VariantProps<typeof comboboxAnchorVariants>;
